import { type MouseEvent, type ReactNode, useId, useState } from 'react';

import { useBreakpointDown } from '../../../hooks';
import type { BrandIdentity } from '../../molecules/BrandLockup';
import { APP_SHELL_COMPACT_BREAKPOINT } from './appShellLayout';

export type AppShellSearch = {
  /** Accessible name and visible text of the search trigger, e.g. "Rechercher". */
  label: string;
  onOpen: () => void;
  /** Keyboard shortcut shown in the sidebar trigger, e.g. "Ctrl K". */
  shortcutHint?: string;
  /** Value of `aria-keyshortcuts`, e.g. "Control+K Meta+K". */
  ariaKeyShortcuts?: string;
};

export type AppShellAssistant = {
  /** Visible text of the sidebar entry and accessible name of the top bar button, e.g. "Demander à l'assistant". */
  label: string;
  href: string;
  onClick?: (event: MouseEvent<HTMLElement>) => void;
  /** The assistant page is the current page. */
  selected?: boolean;
};

export type AppShellAccountMenuItem = {
  id: string;
  label: string;
  icon: ReactNode;
  href?: string;
  onClick?: (event: MouseEvent<HTMLElement>) => void;
  /** Separates a closing item (e.g. "Se déconnecter") from the ones above. */
  dividerBefore?: boolean;
};

export type AppShellAccount = {
  /** Who is signed in, e.g. the e-mail address. Truncated when too long. */
  label: string;
  /** Initials shown in the avatar, e.g. "CD". */
  initials?: string;
  /** Role in the current workspace, e.g. "Admin". */
  detail?: string;
  /** Account actions (`IconButton`s) next to the label, when there is no menu. */
  actions?: ReactNode;
  /** Turns the whole account block into a button opening these items (Mon compte, Se déconnecter). */
  menuItems?: AppShellAccountMenuItem[];
  /** Accessible name of the account button, completed with the label. Defaults to "Mon compte". */
  menuLabel?: string;
};

export type AppShellSwitchSpace = {
  /** Visible hint under the workspace name, e.g. "Changer d'espace". */
  label: string;
  href: string;
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
};

export type AppShellProps = {
  /**
   * Product signature at the top of the sidebar (logo and name; the slogan stays on entry screens).
   * With it, global `actions` share its row and the workspace name gets the full width under it.
   */
  brand?: Pick<BrandIdentity, 'name' | 'logo'>;
  /** Name of the current workspace, shown in the sidebar header and the phone top bar. */
  title: string;
  /** Makes the sidebar workspace name a link to the workspace picker. */
  switchSpace?: AppShellSwitchSpace;
  /** Main navigation, usually a `NavigationList`. Links close the phone drawer when followed. */
  navigation: ReactNode;
  /** Global actions (e.g. notifications): in the top bar below `md`, in the sidebar header from `md`. Mounted once. */
  actions?: ReactNode;
  search?: AppShellSearch;
  /** Entry point to the AI assistant, next to the search: AI-tinted link in the sidebar, AI button in the top bar. */
  assistant?: AppShellAssistant;
  /** Pinned above the account, apart from the main navigation (e.g. a `NavigationList` with the settings). */
  footer?: ReactNode;
  account?: AppShellAccount;
  children: ReactNode;
  navigationLabel?: string;
  openNavigationLabel?: string;
  closeNavigationLabel?: string;
};

const DEFAULT_NAVIGATION_LABEL = 'Navigation principale';
const DEFAULT_OPEN_NAVIGATION_LABEL = 'Ouvrir la navigation';
const DEFAULT_CLOSE_NAVIGATION_LABEL = 'Fermer la navigation';
const DEFAULT_ACCOUNT_MENU_LABEL = 'Mon compte';
const LINK_SELECTOR = 'a[href]';

export function useAppShell(props: AppShellProps) {
  const isCompact = useBreakpointDown(APP_SHELL_COMPACT_BREAKPOINT);
  const [isDrawerRequested, setIsDrawerRequested] = useState(false);
  const drawerId = useId();
  const accountButtonId = useId();
  const accountMenuId = useId();
  const [accountAnchor, setAccountAnchor] = useState<HTMLElement | null>(null);

  function handleOpenDrawer() {
    setIsDrawerRequested(true);
  }

  function handleCloseDrawer() {
    setIsDrawerRequested(false);
  }

  /** Following a link from the phone drawer closes it; buttons (e.g. a sub-menu toggle) keep it open. */
  function handleNavigationClick(event: MouseEvent<HTMLElement>) {
    if (!isCompact) {
      return;
    }
    const target = event.target;
    if (target instanceof Element && target.closest(LINK_SELECTOR)) {
      setIsDrawerRequested(false);
    }
  }

  function handleAccountOpen(event: MouseEvent<HTMLElement>) {
    setAccountAnchor(event.currentTarget);
  }

  function handleAccountClose() {
    setAccountAnchor(null);
  }

  function handleAccountItemClick(item: AppShellAccountMenuItem, event: MouseEvent<HTMLElement>) {
    item.onClick?.(event);
    setAccountAnchor(null);
    setIsDrawerRequested(false);
  }

  function handleSwitchSpaceClick(event: MouseEvent<HTMLAnchorElement>) {
    props.switchSpace?.onClick?.(event);
    setIsDrawerRequested(false);
  }

  function handleAssistantClick(event: MouseEvent<HTMLElement>) {
    props.assistant?.onClick?.(event);
    setIsDrawerRequested(false);
  }

  function handleSearchOpen() {
    setIsDrawerRequested(false);
    props.search?.onOpen();
  }

  return {
    isCompact,
    isDrawerOpen: isCompact && isDrawerRequested,
    drawerId,
    navigationLabel: props.navigationLabel ?? DEFAULT_NAVIGATION_LABEL,
    openNavigationLabel: props.openNavigationLabel ?? DEFAULT_OPEN_NAVIGATION_LABEL,
    closeNavigationLabel: props.closeNavigationLabel ?? DEFAULT_CLOSE_NAVIGATION_LABEL,
    handleOpenDrawer,
    handleCloseDrawer,
    handleNavigationClick,
    handleSearchOpen,
    handleAssistantClick,
    accountButtonId,
    accountMenuId,
    accountAnchor,
    isAccountOpen: accountAnchor !== null,
    accountMenuLabel: props.account?.menuLabel ?? DEFAULT_ACCOUNT_MENU_LABEL,
    handleAccountOpen,
    handleAccountClose,
    handleAccountItemClick,
    handleSwitchSpaceClick,
  };
}
